/* eslint-disable react/no-unstable-nested-components */

import {
  Box,
  ButtonGroup,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToken,
} from '@chakra-ui/react';
import {
  ColumnDef,
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { Expert, ListExpertsQueryVariables } from '@/client/services/api/graphql/gql/graphql';
import { useMemo, useState } from 'react';

import ArchiveExpertModal from '../overlay/ArchiveExpertModal';
import ConfirmationModal from '@/client/components/admin/shared/overlay/confirmation-modals/ConfirmationModal';
import ExpertsColorChangeIcon from '../ExpertsColorChangeIcon';
import ScrollingPagination from '@/client/components/admin/tables/pagination/ScrollingPagination';
import { Search } from '@/client/components/icons/ContinuIcons';
import SolidButton from '@/client/components/shared/buttons/SolidButton';
import StatusCircle from '@/client/components/icons/StatusCircle';
import WarmNeutralEllipsisMenu from '@/client/components/shared/menus/WarmNeutralEllipsisMenu';
import { hexToRGBA } from '@/client/utils/hexToRGBA';
import { useDisableExpert } from '../hooks/useDisableExpert';
import { useEnableExpert } from '../hooks/useEnableExpert';
import { useExpertsStore } from '../expertsStore';
import { useGetPublicImage } from '@/client/services/hooks/media/useGetPublicImage';
import { useListExperts } from '../hooks/useListExperts';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function TabButton({
  label,
  onClick,
  isActive,
}: {
  label: string;
  onClick: () => void;
  isActive: boolean;
}) {
  const [brandButton] = useToken('colors', ['brand.button']);

  return (
    <SolidButton
      onClick={onClick}
      sx={{
        color: isActive ? 'white' : 'neutral.1300',
        backgroundColor: isActive ? 'brand.button' : hexToRGBA(brandButton, 0.1),
        _hover: {
          backgroundColor: isActive ? 'brand.button' : hexToRGBA(brandButton, 0.2),
        },
      }}
    >
      {label}
    </SolidButton>
  );
}

export default function ExpertsTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setIsEditingExpert, selectedExpertId, setSelectedExpertId, setArchiveModalIsOpen } =
    useExpertsStore();
  const { getPublicImage } = useGetPublicImage();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'enabled' | 'disabled'>('enabled');
  const [unpublishModalIsOpen, setUnpublishModalIsOpen] = useState(false);
  const [publishModalIsOpen, setPublishModalIsOpen] = useState(false);

  const { enableExpertMutation } = useEnableExpert();
  const { status: enableExpertStatus } = enableExpertMutation;

  const { disableExpertMutation } = useDisableExpert();
  const { status: disableExpertStatus } = disableExpertMutation;

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });

  const queryPayload = useMemo<ListExpertsQueryVariables>(() => {
    const payload: ListExpertsQueryVariables = {
      enabled: { eq: activeTab === 'enabled' },
      includeTotalCount: true,
      page: pageIndex,
      pageSize,
    };

    if (searchTerm) {
      payload.name = { contains: searchTerm };
    }

    return payload;
  }, [pageIndex, pageSize, searchTerm, activeTab]);

  const { data } = useListExperts(queryPayload);

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  );

  const columnHelper = createColumnHelper<Expert>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo<ColumnDef<Expert, any>[]>(
    () => [
      columnHelper.display({
        id: 'name',
        header: `${t('admin.aiAgent.experts.tableHeader.name')}`,
        cell: (info) => {
          const expert = info.row.original;
          return (
            <HStack>
              <ExpertsColorChangeIcon color={expert.color} />

              <Text
                fontSize="14px"
                fontWeight={500}
                color="baseBlack"
                _groupHover={{ textDecoration: 'underline' }}
              >
                {expert.name}
              </Text>
            </HStack>
          );
        },
      }),
      columnHelper.accessor('resourcesCount', {
        id: 'sources',
        header: `${t('admin.aiAgent.experts.tableHeader.sources')}`,
        cell: (info) => (
          <Text fontSize="14px" fontWeight={500} color="baseBlack">
            {info.getValue() ?? 0} {info.getValue() === 1 ? 'source' : 'sources'}
          </Text>
        ),
      }),
      columnHelper.accessor('enabled', {
        id: 'status',
        header: `${t('admin.aiAgent.experts.tableHeader.status')}`,
        cell: (info) => {
          const isEnabled = info.getValue();
          return (
            <HStack>
              <StatusCircle variant={isEnabled ? 'success' : 'neutral'} />

              <Text fontSize="14px" fontWeight={500} color={isEnabled ? '#00AF4F' : 'neutral.500'}>
                {isEnabled ? t('global.enabled') : t('global.disabled')}
              </Text>
            </HStack>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => {
          const expert = info.row.original;

          return (
            <WarmNeutralEllipsisMenu
              options={[
                {
                  label: t('global.actions.edit'),
                  onClick: (e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    setIsEditingExpert(true);

                    navigate(`/admin/eddy/experts/details/${expert.id}`);
                  },
                },
                {
                  label: expert.enabled
                    ? t('admin.aiAgent.experts.table.actions.sendToDrafts')
                    : t('admin.aiAgent.experts.table.actions.publish'),
                  onClick: (e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    setSelectedExpertId(expert.id);

                    if (expert.enabled) {
                      setUnpublishModalIsOpen(true);
                    } else {
                      setPublishModalIsOpen(true);
                    }
                  },
                },
                {
                  label: t('global.actions.delete'),
                  isDisabled: expert.enabled,
                  disabledTooltipText: `${t(
                    'admin.aiAgent.experts.table.publishedCannotBeDeleted',
                  )}`,
                  onClick: (e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    setSelectedExpertId(expert.id);
                    setArchiveModalIsOpen(true);
                  },
                },
              ]}
            />
          );
        },
      }),
    ],
    [columnHelper],
  );

  const table = useReactTable({
    data: data?.edges.map((edge) => edge.node) ?? [],
    columns,
    pageCount: Math.ceil((data?.pageInfo.totalCount ?? 0) / pageSize),
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
  });

  const pagesArr = Array.from({ length: table.getPageCount() }).map((_, index) => ({
    label: `${index + 1}`,
    value: index,
  }));

  return (
    <Box position="relative">
      <Stack spacing={4}>
        <Flex justifyContent="space-between" alignItems="center">
          <ButtonGroup>
            <TabButton
              label={t('admin.aiAgent.experts.table.filter.published')}
              onClick={() => {
                setActiveTab('enabled');
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
              isActive={activeTab === 'enabled'}
            />
            <TabButton
              label={t('admin.aiAgent.experts.table.filter.drafts')}
              onClick={() => {
                setActiveTab('disabled');
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
              isActive={activeTab === 'disabled'}
            />
          </ButtonGroup>

          {data && data.edges.length > 0 && (
            <InputGroup maxWidth={500}>
              <InputLeftElement pointerEvents="none">
                <Search color="neutral.100" />
              </InputLeftElement>

              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="create"
                placeholder={`${t('admin.aiAgent.experts.table.search.placeholder')}`}
              />
            </InputGroup>
          )}
        </Flex>

        {data && data.edges.length > 0 ? (
          <TableContainer>
            <Table>
              <Thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th
                        key={header.id}
                        fontSize="14px"
                        fontWeight={500}
                        color="baseBlack"
                        textTransform="none"
                        paddingLeft={0}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>

              <Tbody>
                {table.getRowModel().rows.map((row) => (
                  <Tr
                    key={row.id}
                    role="group"
                    _hover={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/admin/eddy/experts/details/${row.original.id}`)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Td key={cell.id} paddingLeft={0}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        ) : (
          <Text fontSize="16px" fontWeight={600} color="neutral.800" textAlign="center">
            {activeTab === 'enabled' ? 'No published Experts' : 'No draft Experts'}
          </Text>
        )}
      </Stack>

      {table.getPageCount() > 1 && (
        <ScrollingPagination
          table={table}
          pagination={pagination}
          pagesArr={pagesArr}
          paddingX={0}
          paddingY={4}
          positionStyles={{}}
        />
      )}

      <ConfirmationModal
        isOpen={!!selectedExpertId && unpublishModalIsOpen}
        onClose={() => {
          setSelectedExpertId('');
          setUnpublishModalIsOpen(false);
        }}
        imageSrc={getPublicImage('continu/app-images/add-files-E-commerce.svg')}
        title={t('admin.aiAgent.experts.unpublishModal.title')}
        descriptionOne={t('admin.aiAgent.experts.unpublishModal.descriptionOne')}
        submitButtonOptions={{
          label: t('admin.aiAgent.experts.unpublishModal.submitButtonLabel'),
          isLoading: disableExpertStatus === 'loading',
          onClick: () => {
            disableExpertMutation.mutateAsync(selectedExpertId);
            setSelectedExpertId('');
            setUnpublishModalIsOpen(false);
          },
        }}
      />

      <ConfirmationModal
        isOpen={!!selectedExpertId && publishModalIsOpen}
        onClose={() => {
          setSelectedExpertId('');
          setPublishModalIsOpen(false);
        }}
        imageSrc={getPublicImage('continu/app-images/Approval-2--Streamline-Brooklyn.svg')}
        title={t('admin.aiAgent.experts.publishModal.title')}
        descriptionOne={t('admin.aiAgent.experts.publishModal.descriptionOne')}
        submitButtonOptions={{
          label: t('admin.aiAgent.experts.publishModal.submitButtonLabel'),
          isLoading: enableExpertStatus === 'loading',
          onClick: () => {
            enableExpertMutation.mutateAsync(selectedExpertId);
            setSelectedExpertId('');
            setPublishModalIsOpen(false);
          },
        }}
      />

      <ArchiveExpertModal />
    </Box>
  );
}
